// eslint-disable-next-line @typescript-eslint/no-restricted-imports
/* eslint-disable rxjs/finnish */
import './pique-action-plan-timeline';

import type { EditorHost } from '@blocksuite/block-std';
import type { ActionPlanStep, ActionPlanTimelineEditDetail } from './pique-action-plan-timeline';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import type {
  DatabaseView,
  RelatedDoc,
} from './types';

/**
 * Custom action panel component that will be injected into the detail panel
 */
@customElement('pique-action-panel')
export class PiqueActionPanel extends LitElement {
  static override styles = css`
    :host {
      display: block;
      padding: 16px;
      background-color: var(--affine-background-primary-color);
      margin-top: 16px;
      max-width: var(--affine-editor-width);
      width: 100%;
      overflow: hidden;
      position: relative;
    }

    h3 {
      font-size: 14px;
      font-weight: 500; /* Reduced font-weight */
      color: var(--affine-text-primary-color);
      margin-top: 16px;
      margin-bottom: 10px;
    }
  `;

  @property({ attribute: false })
  accessor host!: EditorHost;

  @property({ attribute: false })
  accessor view!: DatabaseView;

  @state()
  override accessor title: string = '';

  @state()
  accessor status: string = '';

  @state()
  accessor detailElement: Element | null = null;

  @state()
  accessor breakdownSteps: ActionPlanStep[] = [];

  @state()
  accessor relatedDocs: RelatedDoc[] = [];

  @state()
  accessor isLoadingRelatedDocs: boolean = false;

  private intervalId: number | null = null;
  private _statusColumnId: string | null = null;
  private _statusSubscription: { unsubscribe: () => void } | null = null;

  /**
   * Get the root service directly from host
   */
  get rootService() {
    return this.host?.std.spec.getService('affine:page');
  }

  get quickSearchService() {
    return this.rootService?.quickSearchService;
  }

  /**
   * Gets the row ID from the detail element
   */
  get rowId(): string {
    if (!this.detailElement) return '';

    // Try to read rowId from the element property first
    const detailEl = this.detailElement as any;
    if (detailEl.rowId) {
      return detailEl.rowId;
    }

    // Fall back to data attribute if needed
    return detailEl.dataset?.rowId || '';
  }

  /**
   * Extracts the title from the detail element
   */
  private getTitleFromElement(): string {
    if (!this.detailElement) return '';

    // Try to find the title in the rich text element
    const titleElement = this.detailElement.querySelector(
      'database-datasource-block-renderer rich-text'
    );
    if (titleElement) {
      return titleElement.textContent?.trim() || '';
    }

    return '';
  }

  /**
   * Extracts the status from the detail element
   */
  private getStatusFromElement(): string {
    if (!this.detailElement) return '';

    // Find the status field - usually with a data-column-id
    const statusField = this.detailElement.querySelector(
      'affine-data-view-record-field .select-selected'
    );

    if (statusField) {
      return statusField.textContent?.trim() || '';
    }

    return '';
  }

  /**
   * Finds the ID of the 'Status' column.
   */
  private _findStatusColumnId(): string | null {
    // Added optional chaining for columns$
    if (!this.view || !this.view.columns$?.value) return null;

    const columnIds = this.view.columns$.value;
    for (const id of columnIds) {
      const column = this.view.columnGet(id);
      if (column && column.name?.toLowerCase() === 'status') {
        return id;
      }
    }
    return null;
  }

  /**
   * Subscribes to changes in the status cell of the current row.
   */
  private _subscribeToStatus(): void {
    // Clear existing subscription
    if (this._statusSubscription) {
      this._statusSubscription.unsubscribe();
      this._statusSubscription = null;
    }

    // We need view and rowId to subscribe
    if (!this.view || !this.rowId) {
      this.status = this.getStatusFromElement(); // Fallback if no view/rowId
      return;
    }

    // Find the status column ID if we don't have it
    if (!this._statusColumnId) {
      this._statusColumnId = this._findStatusColumnId();
    }

    // If still no status column, just use the DOM value
    if (!this._statusColumnId) {
      this.status = this.getStatusFromElement(); // Fallback if column not found
      return;
    }

    const cell = this.view.cellGet(this.rowId, this._statusColumnId);
    // Added optional chaining for stringValue$
    if (cell && cell.stringValue$) {
      // Set initial status from the reactive source
      this.status = cell.stringValue$.value || '';

      // Subscribe to changes
      this._statusSubscription = cell.stringValue$.subscribe(
        (newStatus: string) => {
          this.status = newStatus || '';
        }
      );
    } else {
      // Fallback if cell or stringValue$ not found
      this.status = this.getStatusFromElement();
    }
  }

  private handleStepEdit(e: CustomEvent<ActionPlanTimelineEditDetail>): void {
    e.stopPropagation();
    this.breakdownSteps = e.detail.steps.map(step => ({
      ...step,
      id: String(step.id),
      completed: Boolean(step.completed),
    }));
  }

  override connectedCallback() {
    super.connectedCallback();

    // Query for the detail element periodically
    this.intervalId = window.setInterval(() => {
      this.queryDetailElement();
    }, 500);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    if (this.intervalId !== null) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private queryDetailElement(): void {
    const detail = document.querySelector('affine-data-view-record-detail');
    if (detail) {
      this.detailElement = detail;

      this.title = this.getTitleFromElement();

      this._subscribeToStatus();

      // if (
      //   this.title &&
      //   this.relatedDocs.length === 0 &&
      //   !this.isLoadingRelatedDocs
      // ) {
      //   this.fetchRelatedDocs().catch(console.error);
      // }

      if (this.intervalId !== null) {
        window.clearInterval(this.intervalId);
        this.intervalId = null;
      }
    }
  }

  override render() {
    if (!this.detailElement) {
      return nothing;
    }

    if (this.breakdownSteps.length === 0) {
      return nothing;
    }

    return html`
      <pique-action-plan-timeline
        .steps=${this.breakdownSteps}
        .editable=${true}
        @pique-timeline-step-edit=${this.handleStepEdit}
      ></pique-action-plan-timeline>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pique-action-panel': PiqueActionPanel;
  }
}
